import React, { useEffect, useState } from "react";
import CustomTable from "../../components/customtable/CustomTable";
import DebounceSearch from "../../components/common/DebounceSearch";
import Breadcrumb from "../../components/common/Breadcrumb";
import Action from "../../components/common/Action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteAudio, getAudioList } from "../../redux/audio/slice";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { SortDownArrow } from "../../assets/svg";

const Audio = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [audioDelete, setAudioDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);

  // selector
  const { audioList, audioListLoading } = useSelector((store) => ({
    audioList: store?.audioData?.getAudioListStatus?.data?.data,
    audioListLoading: store?.audioData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getAudioList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [dispatch, searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  // Delete movement
  const deleteAudioId = () => {
    setIsLoading(true);
    if (!audioDelete) return;
    dispatch(deleteAudio(audioDelete)).then((res) => {
      setIsLoading(false);
      if (res.type === "deleteAudio/fulfilled") {
        dispatch(
          getAudioList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
      setConfirmDelete(false);
    });
  };
  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="w-16 table-th">No.</th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("audioName");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "audioName"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("title");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Title</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "title"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">Duration</th>
      <th className="w-24 table-th">Actions</th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(audioList?.data || []).map((item, index) => (
        <tr key={item.id} className=" shadow-sm text-mgrey hover:bg-navTheme/5">
          <td className="px-5 py-3">
            {(pageCount - 1) * pageLimit + index + 1}
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-1">{item?.audioName || "-"}</div>
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-2" title={item?.title}>
              {item?.title || "-"}
            </div>
          </td>
          <td className="px-5 py-3">{item?.audioduration || "-"}</td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/add-edit-audio", {
                        state: { audio: item, mode: "edit" },
                      })
                    }
                  >
                    View / Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setAudioDelete(item?._id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );
  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">Audio</h6>
        </div>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={"Audio"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[7px] rounded uppercase font-Proxima text-base w-40 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            onClick={() => {
              navigate("/add-edit-audio");
            }}
            type="button"
          >
            + Add
          </button>
        </div>
        <div className="w-full bg-white shadow rounded-lg">
          <CustomTable
            overflowVisibleLg={true}
            columnHeaders={<ColumnHeaders />}
            dataRows={<DataRows />}
            data={audioList}
            loading={audioListLoading}
            showPagination={true}
            disableNext={!!audioList?.data?.length}
            disablePrev={!!audioList?.data?.length}
            setPaginationCurrentLimit={setPageLimit}
            paginationCurrentLimit={pageLimit}
            current={pageCount}
            total={audioList?.pagination?.total}
            paginationData={(crPage) => setPageCount(crPage)}
            lastPage={Math.ceil(audioList?.pagination?.total / pageLimit)}
          />
        </div>
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteAudioId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default Audio;
